'use strict';
import isArray from 'lodash/isArray';
import isDate from 'lodash/isDate';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import memoize from 'lodash/memoize';
import toLower from 'lodash/toLower';


/**
 * Replace the contents of an array with the contents of the source array without changing the array's reference.
 * @param {Array} array - Output. The array to update
 * @param {Array | Object} source - Input. The source data array or object. Its contents will be placed into the output array
 * @param {number} [maxCount] - If provided, it will place a maximum on the number of elements copied into the output.
 */
export function replaceArrayContents(array, source, maxCount) {
  if (!isArray(array) || (!isArray(source) && !isObject(source))) {
    throw new Error(`replaceArrayContents requires 2 arguments! The first must be an array. The second must be an array or object. Received '${typeof array} and ${typeof source}`);
  }

  maxCount = isNumber(maxCount) ? maxCount : Number.MAX_VALUE;
  let keys;
  let i = array.length;
  while (i--) {
    array.pop();
  }

  if (isArray(source)) {
    i = Math.min(source.length, maxCount);
    while (i--) {
      array[i] = source[i];
    }
  } else {
    keys = Object.keys(source);
    i = Math.min(keys.length, maxCount);
    while (i--) {
      array[i] = source[keys[i]];
    }
  }
}

export function sortByName(list) {
  if (!list) {
    return list;
  } else {
    list.sort(sortByProperty('name', true));
    return list;
  }
}

export function sortByProperty(propertyName, ascending, ignoreCase) {
  return function (o1, o2) {
    let x1 = o1[propertyName];
    if (isDate(x1)) x1 = x1.getTime();
    let x2 = o2[propertyName];
    if (isDate(x2)) x2 = x2.getTime();
    return naturalCompare(x1, x2, ascending, ignoreCase);
  }
}

export function sortByPropertyAccessor(accessFn, ascending, ignoreCase) {
  return function (o1, o2) {
    let x1 = accessFn(o1);
    if (isDate(x1)) x1 = x1.getTime();
    let x2 = accessFn(o2);
    if (isDate(x2)) x2 = x2.getTime();
    return naturalCompare(x1, x2, ascending, ignoreCase);
  }
}

/*
 * Original Source: http://web.archive.org/web/20130827210000/http://www.overset.com/2008/09/01/javascript-natural-sort-algorithm/
 * Natural Sort algorithm for Javascript - Version 0.7 - Released under MIT license
 * Author: Jim Palmer (based on chunking idea from Dave Koelle)
 *
 * Modified to accommodate ascending/descending sort and optional ignoreCase flags
 */
export function naturalCompare(a, b, ascending, ignoreCase) {
  var ascendingFlag = ascending ? 1 : -1;
  var re = /(^-?[0-9]+(\.?[0-9]*)[df]?e?[0-9]?$|^0x[0-9a-f]+$|[0-9]+)/gi,
    sre = /(^[ ]*|[ ]*$)/g,
    dre = /(^([\w ]+,?[\w ]+)?[\w ]+,?[\w ]+\d+:\d+(:\d+)?[\w ]?|^\d{1,4}[\/\-]\d{1,4}[\/\-]\d{1,4}|^\w+, \w+ \d+, \d{4})/,
    hre = /^0x[0-9a-f]+$/i,
    ore = /^0/,
    i = function (s) {
      return ignoreCase && ('' + s).toLowerCase() || '' + s
    },
    // convert all to strings strip whitespace
    x = i(a).replace(sre, '') || '',
    y = i(b).replace(sre, '') || '',
    // chunk/tokenize
    xN = x.replace(re, '\0$1\0').replace(/\0$/, '').replace(/^\0/, '').split('\0'),
    yN = y.replace(re, '\0$1\0').replace(/\0$/, '').replace(/^\0/, '').split('\0'),
    // numeric, hex or date detection
    xD = parseInt(x.match(hre)) || (xN.length != 1 && x.match(dre) && Date.parse(x)),
    yD = parseInt(y.match(hre)) || xD && y.match(dre) && Date.parse(y) || null,
    oFxNcL, oFyNcL;
  // first try and sort Hex codes or Dates
  if (yD)
    if (xD < yD) return -ascendingFlag;
    else if (xD > yD) return ascendingFlag;
  // natural sorting through split numeric strings and default strings
  for (let cLoc = 0, numS = Math.max(xN.length, yN.length); cLoc < numS; cLoc++) {
    // find floats not starting with '0', string or 0 if not defined (Clint Priest)
    oFxNcL = !(xN[cLoc] || '').match(ore) && parseFloat(xN[cLoc]) || xN[cLoc] || 0;
    oFyNcL = !(yN[cLoc] || '').match(ore) && parseFloat(yN[cLoc]) || yN[cLoc] || 0;
    // handle numeric vs string comparison - number < string - (Kyle Adams)
    if (isNaN(oFxNcL) !== isNaN(oFyNcL)) {
      return (isNaN(oFxNcL)) ? ascendingFlag : -ascendingFlag;
    }
    // rely on string comparison if different types - i.e. '02' < 2 != '02' < '2'
    else if (typeof oFxNcL !== typeof oFyNcL) {
      oFxNcL += '';
      oFyNcL += '';
    }
    if (oFxNcL < oFyNcL) return -ascendingFlag;
    if (oFxNcL > oFyNcL) return ascendingFlag;
  }
  return 0;
}

export function toArray(object) {
  if (!object) {
    return []
  } else {
    const keys = Object.keys(object);
    let i = keys.length;
    const array = new Array(i);
    while (i--) {
      array[i] = object[keys[i]];
    }

    return array;
  }
}

export function filterWithToString(list, query) {
  if (!query || typeof query !== 'string') return list;

  query = toLower(query);

  const result = [];
  let item;

  for (let i = 0, length = list.length; i < length; i++) {
    item = list[i];

    if (item && isObject(item) && toLower(item.toString()).indexOf(query) > -1) {
      result.push(item);
    }
  }

  return result;
}

export function filterByProperty(propertyName) {
  return function (list, query) {
    if (!query || typeof query !== 'string') return list;

    query = toLower(query);

    let result = [];
    let item;

    for (let i = 0, length = list.length; i < length; i++) {
      item = list[i];

      if (item && isObject(item) && item[propertyName] && toLower(item[propertyName]).indexOf(query) > -1) {
        result.push(item);
      }
    }

    return result;
  }
}

/**
 *
 * @param list
 * @param privateList
 * @return {Array}
 */
export function combineLists(list, privateList) {
  const result = [];
  let key, item;

  for (key in list) {
    if (list.hasOwnProperty(key)) {
      item = list[key];
      if (isObject(item)) {
        item.$id = key;
        item.isPrivate = false;
        result.push(item);
      }
    }
  }

  for (key in privateList) {
    if (privateList.hasOwnProperty(key)) {
      item = privateList[key];
      if (isObject(item)) {
        item.$id = key;
        item.isPrivate = true;
        result.push(item);
      }
    }
  }

  return result;
}

/**
 * Use with Array.prototype.reduce to convert an array to an object.
 * Assumes the presence of an 'id' property.
 *
 * @param {Object} acc
 * @param {Object} record
 * @return {*}
 */
export function toObject(acc, record) {
  acc[record.id] = record;
  return acc;
}

export const abbreviate = makeAbbreviate();

export function makeAbbreviate() {
  return input => isString(input) ? input.charAt(0).toUpperCase() : void 0;

  // return memoize(abr);
  //
  // function abr(input, all) {
  //   if (isString(input)) {
  //     var result = input.replace(/([^\W_]+[^\s-]*)[\s-]*/g, extract);
  //
  //     var len = result.length;
  //     return len > 2 ? result.substring(0, 1) + result.substring(len - 1, len) : result;
  //   }
  // }
  //
  // function extract(txt) {
  //   return txt.charAt(0).toUpperCase();
  // }
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/filters/filterUtils.js